/* eslint-env jest */
import eligiblePoses, { acceptsLandscape, acceptsJpg } from './poseEligibility';
import { ORIENTATION } from '../../../hooks/useImageOrientation';

const pose = (id, extension, url) => ({ id, extension, watermarkUrl: url || `/p/${id}${extension}` });

const PORTRAIT_PNG = pose('1', '.png');
const PORTRAIT_JPG = pose('2', '.jpg');
const LANDSCAPE_PNG = pose('3', '.png');
const LANDSCAPE_JPG = pose('4', '.jpg');

const ORIENTATIONS = {
  '/p/1.png': { orientation: ORIENTATION.PORTRAIT },
  '/p/2.jpg': { orientation: ORIENTATION.PORTRAIT },
  '/p/3.png': { orientation: ORIENTATION.LANDSCAPE },
  '/p/4.jpg': { orientation: ORIENTATION.LANDSCAPE },
};

const ALL = [PORTRAIT_PNG, PORTRAIT_JPG, LANDSCAPE_PNG, LANDSCAPE_JPG];
const ids = (poses) => poses.map((p) => p.id);

describe('acceptsLandscape', () => {
  it('allows landscape when the column is blank, as every product did before', () => {
    expect(acceptsLandscape({})).toBe(true);
    expect(acceptsLandscape({ allowsLandscapePoses: null })).toBe(true);
    expect(acceptsLandscape(undefined)).toBe(true);
  });

  it('refuses only on an explicit false', () => {
    expect(acceptsLandscape({ allowsLandscapePoses: false })).toBe(false);
    expect(acceptsLandscape({ allowsLandscapePoses: true })).toBe(true);
  });
});

describe('acceptsJpg', () => {
  // ⚠️ THE TRI-STATE IS THE WHOLE POINT. Blank is not "no" -- it defers to the
  // Order-template derivation MultiPose already had, so no existing product
  // changes behaviour.
  it('defers to the derivation when the column is blank', () => {
    expect(acceptsJpg({}, false)).toBe(true);
    expect(acceptsJpg({}, true)).toBe(false);
    expect(acceptsJpg({ allowsJpgPoses: null }, true)).toBe(false);
  });

  it('overrules the derivation in BOTH directions once set', () => {
    expect(acceptsJpg({ allowsJpgPoses: true }, true)).toBe(true);
    expect(acceptsJpg({ allowsJpgPoses: false }, false)).toBe(false);
  });
});

describe('eligiblePoses', () => {
  it('offers everything to a package sheet, which has no add-on rule', () => {
    expect(eligiblePoses(ALL, undefined, ORIENTATIONS)).toEqual(ALL);
  });

  it('offers everything when the product sets neither rule', () => {
    expect(eligiblePoses(ALL, {}, ORIENTATIONS)).toEqual(ALL);
  });

  // The weekly dry-erase magnet: an upright cut-out standing on the artwork.
  it('leaves only the portrait PNG for a product that refuses both', () => {
    const magnet = { allowsLandscapePoses: false, allowsJpgPoses: false };

    expect(ids(eligiblePoses(ALL, magnet, ORIENTATIONS))).toEqual(['1']);
  });

  it('drops landscape only, when JPGs are fine', () => {
    const addon = { allowsLandscapePoses: false };

    expect(ids(eligiblePoses(ALL, addon, ORIENTATIONS))).toEqual(['1', '2']);
  });

  it('drops JPGs only, when landscape is fine', () => {
    const addon = { allowsJpgPoses: false };

    expect(ids(eligiblePoses(ALL, addon, ORIENTATIONS))).toEqual(['1', '3']);
  });

  it('drops JPGs on the Order-template derivation alone, with no column set', () => {
    expect(ids(eligiblePoses(ALL, {}, ORIENTATIONS, true))).toEqual(['1', '3']);
  });

  // ⚠️ MEASURED, NEVER INFERRED FROM THE FILENAME. Orientation is season × crop
  // and a pose number never tells you which.
  it('keeps a pose whose orientation has not been measured yet', () => {
    const addon = { allowsLandscapePoses: false };

    // Nothing measured: the carousel must not empty out on first paint and
    // refill, flickering a pose out from under the customer's cursor.
    expect(ids(eligiblePoses(ALL, addon, {}))).toEqual(['1', '2', '3', '4']);
  });

  it('keeps a pose measured UNKNOWN', () => {
    const addon = { allowsLandscapePoses: false };
    const orientations = { ...ORIENTATIONS, '/p/3.png': { orientation: ORIENTATION.UNKNOWN } };

    expect(ids(eligiblePoses(ALL, addon, orientations))).toContain('3');
  });

  // ⚠️ NEVER AN EMPTY CAROUSEL -- that is a dead end mid-purchase.
  it('falls back to every pose rather than offering none', () => {
    const magnet = { allowsLandscapePoses: false, allowsJpgPoses: false };
    const allLandscapeJpg = [LANDSCAPE_JPG];

    expect(eligiblePoses(allLandscapeJpg, magnet, ORIENTATIONS)).toEqual(allLandscapeJpg);
  });

  it('reads the extension off the url when the pose carries none', () => {
    const addon = { allowsJpgPoses: false };
    const bare = [{ id: '9', watermarkUrl: '/p/9.jpg' }, { id: '8', watermarkUrl: '/p/8.png' }];

    expect(ids(eligiblePoses(bare, addon, {}))).toEqual(['8']);
  });

  // ⚠️ REAL DATA. ~31k production poses carry no extension and a few hundred
  // hold junk from a filename parse that went wrong. Treating those as JPGs
  // would drop a student's actual photo off the magnet, so the url decides.
  it('trusts the url over an unrecognised extension', () => {
    const addon = { allowsJpgPoses: false };
    const messy = [
      { id: 'a', extension: '._torres_0_524040-12', watermarkUrl: '/p/a.png' },
      { id: 'b', extension: null, watermarkUrl: '/p/b.png' },
      { id: 'c', extension: '', watermarkUrl: '/p/c.jpg' },
      { id: 'd', extension: '.7-01', watermarkUrl: '/p/d.jpg' },
    ];

    expect(ids(eligiblePoses(messy, addon, {}))).toEqual(['a', 'b']);
  });

  it('survives a null pose list', () => {
    expect(eligiblePoses(null, { allowsLandscapePoses: false }, {})).toEqual([]);
  });
});
